import { useCallback } from "react";

import { SkyAPI } from "@ai360/core";

import { isSsoRedirectSuppressed } from "./sso-redirect";

/**
 * Looks up the SSO status for an email and, when its last connection lives on another
 * site, navigates there. Resolves true when a redirect was started so the caller can stop
 * (and leave its spinner up); false means "not SSO here, continue with a password".
 * Lookup failures (404 etc.) are treated as non-SSO.
 */
export const useSsoRedirect = (): ((email: string) => Promise<boolean>) => {
    const [triggerSsoStatus] = SkyAPI.useLazyGetSsoStatusQuery();
    const [triggerLastConnection] = SkyAPI.useLazyGetSsoLastConnectionQuery();

    return useCallback(
        async (email: string): Promise<boolean> => {
            try {
                const status = await triggerSsoStatus(email).unwrap();
                if (status?.ssoEnabled && status.status !== SkyAPI.SsoConnectionStatus.Mixed) {
                    const last = await triggerLastConnection(email).unwrap();
                    if (last?.isConnected && last.redirectUrl) {
                        if (isSsoRedirectSuppressed()) {
                            console.warn("SSO redirect suppressed (local dev):", last.redirectUrl);
                        } else {
                            window.location.href = last.redirectUrl;
                            return true;
                        }
                    }
                }
            } catch {
                // 404 / lookup failure → treat as non-SSO, fall through to password
            }
            return false;
        },
        [triggerLastConnection, triggerSsoStatus]
    );
};
